import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Calendar, Loader2, HelpCircle, Building, Users, UserPlus, LogIn, Eye, EyeOff } from "lucide-react";
import { isIOSApp } from "@/utils/platform";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const { login, settings } = useAuth();
  const navigate = useNavigate();

  // Detect if running inside the native iOS app (Capacitor WebView).
  // Per Apple Guideline 3.1.1 / 3.1.3(b), business and employee account
  // creation, plan/subscription information, payment processors and external
  // links to the marketing website must NOT appear inside the iOS app.
  // On web and Android the full UI is available.
  const isIOSAppEnv = isIOSApp();

  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/status`);
      setIsFirstSetup(response.data.is_first_setup);
    } catch (error) {
      console.error("Failed to check system status");
    } finally {
      setCheckingSystem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success("Accesso effettuato con successo!");
      
      // Always land on the Home (Dashboard) after login.
      // Onboarding wizard remains accessible from /settings for titolari who want it.
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  // Help tooltip component
  const HelpTooltip = ({ content }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          type="button" 
          className="inline-flex items-center justify-center ml-2 h-5 w-5 rounded-full bg-theme-100 hover:bg-theme-200 text-theme-600 transition-colors"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" className="max-w-xs p-3 text-sm bg-slate-900 text-white border-slate-800">
        {content}
      </PopoverContent>
    </Popover>
  );

  if (checkingSystem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // FIRST SETUP - No users exist
  if (isFirstSetup) {
    return (
      <div className="min-h-screen grid lg:grid-cols-2">
        {/* Left side - First Setup */}
        <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
          <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <img 
                src="/logo_ferie_permessi.jpg" 
                alt="Ferie e Permessi" 
                className="h-20 w-auto object-contain rounded-lg shadow-md"
              />
              <div>
                <h2 className="text-xl font-bold text-slate-900">Ferie e Permessi</h2>
                <p className="text-sm text-slate-500">Gestione Aziendale Ferie e Permessi</p>
                <p className="text-xs text-slate-400 mt-1">by LuiCas ™</p>
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Benvenuto</h1>
              <p className="text-slate-600">
                Registrati come titolare per creare un nuovo account aziendale
              </p>
            </div>

            {/* Onboarding Instructions */}
            <Card className="card-theme-highlight">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-theme-100 rounded-lg shrink-0">
                    <HelpCircle className="h-5 w-5 text-theme-600" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-900">Come iniziare</h3>
                    <ul className="text-sm text-slate-600 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">1.</span>
                        <span>Registrati come <strong>Titolare</strong> per creare l'azienda</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">2.</span>
                        <span>Configura le impostazioni aziendali (nome, logo, orari)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">3.</span>
                        <span>I dipendenti potranno registrarsi successivamente</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => navigate("/register?role=titolare")}
              data-testid="first-setup-register-btn"
              className="w-full h-12 btn-theme rounded-md btn-press font-medium gap-2"
            >
              <Building className="h-5 w-5" />
              Registrati come Titolare
            </Button>
          </div>
        </div>

        {/* Right side - Image */}
        <div className="hidden lg:block relative">
          <img
            src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
            alt="Team working together"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/20"></div>
        </div>
      </div>
    );
  }

  // NORMAL LOGIN - Users exist
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center text-center gap-4">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-24 w-auto object-contain rounded-lg shadow-md"
            />
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="company-name">Ferie e Permessi</h2>
              <p className="text-sm text-slate-500">Gestione Aziendale Ferie e Permessi</p>
              <p className="text-xs text-slate-400 mt-1">by LuiCas ™</p>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Accedi</h1>
            <p className="text-slate-600">Inserisci le tue credenziali per accedere</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                data-testid="login-email-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-12 pr-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-slate-500 hover:text-slate-900 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className={`w-full h-12 text-white rounded-md btn-press font-medium ${
                settings?.tema_colore 
                  ? "btn-theme" 
                  : "bg-slate-700 hover:bg-slate-600"
              }`}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accesso in corso...</>
              ) : (
                "Accedi"
              )}
            </Button>
          </form>

          {/* On iOS the LoginPage is intentionally a "pure sign-in only" screen,
              with NO mention of registration, plans, subscriptions, website
              links or other external purchase mechanisms — per Apple Guideline
              3.1.1 / 3.1.3(b). On web and Android the registration options
              remain available. */}
          {!isIOSAppEnv && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Oppure</span>
                </div>
              </div>

              <div className="text-center space-y-3">
                <p className="text-slate-600">Non hai un account?</p>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/register?role=titolare")}
                        data-testid="register-titolare-link"
                        className={`w-full h-12 gap-2 text-sm sm:text-base ${
                          settings?.tema_colore
                            ? "btn-theme-light"
                            : "btn-amber-light"
                        }`}
                      >
                        <Building className="h-5 w-5 shrink-0" />
                        <span>Registrati come Titolare</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-center" data-testid="tooltip-register-titolare">
                      <p>
                        Crea il calendario aziendale, gestisci ferie e permessi del tuo team e invita i tuoi dipendenti con un semplice codice. <strong>Gratis per sempre fino a 5 dipendenti</strong> — registrati e scopri quanto è semplice.
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/register")}
                        data-testid="register-dipendente-link"
                        className={`w-full h-12 gap-2 text-sm sm:text-base ${
                          settings?.tema_colore
                            ? "btn-theme-light"
                            : "btn-amber-light"
                        }`}
                      >
                        <UserPlus className="h-5 w-5 shrink-0" />
                        <span>Registrati come Dipendente</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-center" data-testid="tooltip-register-dipendente">
                      <p>
                        Per registrarti come dipendente serve il <strong>codice azienda</strong> fornito dal tuo titolare. Senza codice non potrai completare la registrazione.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </>
          )}

          {/* iOS: solo registrazione dipendente con codice invito (nessun acquisto, nessun piano) */}
          {isIOSAppEnv && (
            <div className="text-center pt-2" data-testid="ios-register-dipendente-block">
              <p className="text-sm text-slate-600">Sei un dipendente?</p>
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="text-slate-900 font-semibold hover:underline text-sm"
                data-testid="ios-register-dipendente-link"
              >
                Registrati con il codice invito della tua azienda
              </button>
            </div>
          )}

          <div className="text-center">
            {/* Privacy Policy Link */}
            <p className="text-xs text-slate-400 mt-4">
              Accedendo, accetti la nostra{" "}
              <a href="/privacy-policy.html" target="_blank" className="text-slate-500 hover:text-slate-700 underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
